"use client";

import { mergeProps, useRender } from "@base-ui/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { ReactNode, useRef } from "react";
import { Container, ContainerProps } from "./container";
import { RenderIf } from "./common/render-if";
import { SeoHeading, SeoHeadingProps } from "./common/seo-heading";
import { Text } from "./ui/text";
import Image, { ImageProps } from "next/image";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";
import Link from "next/link";

const sectionVariants = cva("py-6 sm:py-10 md:py-15", {
  variants: {},
  defaultVariants: {},
});

interface SectionProps
  extends useRender.ComponentProps<"section">, VariantProps<typeof sectionVariants> {
  isFluid?: boolean;
  containerProps?: ContainerProps;
  title?: string;
  seoTitle?: string;
  seoHeadingProps?: Omit<SeoHeadingProps, "heading" | "seoHeading">;
  paragraph?: string;
  color?: "primary" | "secondary";
  bgImage?: string;
  bgImageProps?: Omit<ImageProps, "src" | "alt">;
  path?: string;
  actionLabel?: string;
  noImageOnMobile?: boolean;
  onAction?: () => void;
  sectionChild?: ReactNode;
}

const POPUPS = ["COURSE_DOWNLOAD_BROCHURE"];

export const Section = ({
  render,
  className,
  isFluid,
  children,
  containerProps,
  title,
  seoTitle,
  seoHeadingProps,
  paragraph,
  bgImage,
  bgImageProps,

  color,
  id,
  path,
  actionLabel,
  onAction,
  sectionChild,
  ...props
}: SectionProps) => {
  const hasPopup = POPUPS.includes(path ?? "");

  const handleClick = () => {
    if (typeof onAction === "function") {
      onAction();
    } else {
      console.warn("Pass onAction prop to handle button click event if path is not url");
    }
  };
  const internalRef = useRef<HTMLElement | null>(null);
  const defaultProps: useRender.ElementProps<"section"> = {
    id: id,
    className: cn(
      sectionVariants({
        className: cn(className, "relative", {
          " isolate": Boolean(bgImage),
          "scroll-mt-16": Boolean(id),
          "bg-primary  group": color === "primary",
          "bg-secondary  group": color === "secondary",
        }),
      })
    ),
    children: isFluid ? (
      children
    ) : (
      <>
        {sectionChild}
        <RenderIf condition={Boolean(bgImage)}>
          <Image
            src={bgImage!}
            alt={id || "section-bg"}
            fill
            sizes="(max-width: 767px) 0px, 100vw"
            quality={75}
            {...bgImageProps}
            className={cn("-z-2 object-cover md:object-right", bgImageProps?.className)}
          />
        </RenderIf>
        <Container {...containerProps}>
          <RenderIf
            condition={
              Boolean(title) || Boolean(seoTitle) || (Boolean(path) && Boolean(actionLabel))
            }
          >
            <div className="flex flex-col gap-4 pb-10 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <RenderIf condition={Boolean(title) || Boolean(seoTitle)}>
                  <SeoHeading
                    heading={title}
                    seoHeading={seoTitle}
                    seoHeadingProps={{
                      className: cn({
                        "text-white/60 dark:text-muted-foreground": color === "primary",
                      }),
                      ...seoHeadingProps,
                    }}
                    headingProps={{
                      className: cn({
                        "text-background dark:text-foreground": color === "primary",
                      }),
                    }}
                  />
                </RenderIf>
                <RenderIf condition={Boolean(paragraph)}>
                  <Text
                    className={cn("mb-6 max-w-4xl", {
                      "text-primary-foreground": color === "primary",
                    })}
                  >
                    {paragraph}
                  </Text>
                </RenderIf>
              </div>
              <RenderIf condition={Boolean(path) && Boolean(actionLabel)}>
                <Button
                  variant={"outline"}
                  className="rounded-full"
                  onClick={hasPopup ? handleClick : undefined}
                  nativeButton={hasPopup}
                  render={hasPopup ? <button /> : <Link href={path || "#"} />}
                >
                  {actionLabel} <ArrowRight />
                </Button>
              </RenderIf>
            </div>
          </RenderIf>
          {children}
        </Container>
      </>
    ),
  };

  const element = useRender({
    defaultTagName: "section",
    render,
    ref: internalRef,
    props: mergeProps<"section">(defaultProps, props),
    state: {
      slot: "section",
    },
  });

  return element;
};
